// SAM-1109 — the attendee list of an event shows one row per person, but every row of a pass carries the pass's
// accessId. These helpers say who is a companion of whom, paint the status as a chip and match the person a
// row stands for inside the pass that opens.

export interface AttendeeLike {
  accessId: string;
  isMainGuest: boolean;
  name: string;
  lastName: string;
  idnum?: string;
}

/** "Penelope G.": first given name and the initial of the last name. */
export const shortName = (name: string, lastName: string): string => {
  const given = name.trim().split(/\s+/)[0] || '';
  const surname = lastName.trim() || name.trim().split(/\s+/)[1] || '';
  return [given, surname ? `${surname[0].toUpperCase()}.` : ''].filter(Boolean).join(' ');
};

/** Main guest of every pass, by accessId. */
export const mainGuestsByAccess = <T extends AttendeeLike>(rows: T[]): Map<string, T> =>
  new Map(rows.filter((r) => r.isMainGuest).map((r) => [r.accessId, r]));

/** Companions in each pass, by accessId. */
export const companionCounts = (rows: AttendeeLike[]): Map<string, number> => {
  const out = new Map<string, number>();
  rows.forEach((r) => { if (!r.isMainGuest) out.set(r.accessId, (out.get(r.accessId) ?? 0) + 1); });
  return out;
};

/** The line under the name: "Main guest", "Main guest · +2 companions" or "Companion of Penelope G.". */
export const attendeeSubtitle = (row: AttendeeLike, mains: Map<string, AttendeeLike>, counts: Map<string, number>): { text: string; companion: boolean } => {
  if (!row.isMainGuest) {
    const main = mains.get(row.accessId);
    return { text: main ? `Companion of ${shortName(main.name, main.lastName)}` : 'Companion', companion: true };
  }
  const n = counts.get(row.accessId) ?? 0;
  return { text: n ? `Main guest · +${n} companion${n === 1 ? '' : 's'}` : 'Main guest', companion: false };
};

const norm = (s: string | undefined) => (s || '').trim().replace(/\s+/g, ' ').toLowerCase();
const fullName = (g: { name?: string; last_name?: string }) => norm(`${g.name || ''} ${g.last_name || ''}`);

/** Is this guest of the pass the person the clicked row stands for? Same name, and the same ID number when both have one. */
export const isSamePerson = (guest: { name?: string; last_name?: string; idnum?: string }, row: Pick<AttendeeLike, 'name' | 'lastName' | 'idnum'>): boolean => {
  if (fullName(guest) !== norm(`${row.name} ${row.lastName}`)) return false;
  return !guest.idnum || !row.idnum || norm(guest.idnum) === norm(row.idnum);
};

export interface StatusChipStyle { bg: string; fg: string }

/** Colour by what the status says (the label comes from the live status records, never from the number). */
export const statusChipStyle = (label: string): StatusChipStyle => {
  const l = label.trim().toLowerCase();
  if (/pending|wait/.test(l)) return { bg: '#FEF3C7', fg: '#92400E' };
  if (/cancel|reject|denied|expired/.test(l)) return { bg: '#FEE2E2', fg: '#991B1B' };
  if (/confirm|approved|verified/.test(l)) return { bg: '#D1FAE5', fg: '#065F46' };
  if (/check/.test(l)) return { bg: '#DBEAFE', fg: '#1E40AF' };
  return { bg: '#F3F4F6', fg: '#374151' };
};
